import React, { useCallback, useMemo, useRef } from 'react';
import {
  RichTextEditor,
  RichTextEditorRef,
} from '@cotera/client/app/components/app/rte';
import type { EditorExtensionConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import {
  AttachmentNode,
  CoteraMention,
  Disclaimer,
  EmojiSuggestionMenu,
  ExpressionNode,
  JsonTypeBuilderNode,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { MentionResourcePicker } from '@cotera/client/app/components/resource-picker';
import { useOpenToolModal } from '@cotera/client/v0/components/tool-connections';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useCocoMentions } from '../../hooks/use-coco-mentions';
import { useOpenResourceMention } from '../../hooks/use-open-resource-mention';
import { useDocumentModel } from './document.model';
import { SaveDocumentAction } from './save-document.action';
import { useContinualSave } from '@cotera/client/app/hooks/use-continual-save';
import { useWhoami } from '@cotera/client/app/stores/org';
import { RESOURCE_MEASURE } from '../resources/resource-page-framing';

const makePlaceholder = ({
  editor,
  node,
}: {
  editor: TiptapEditor;
  node: {
    type: { name: string };
    attrs: Record<string, unknown>;
    textContent: string;
  };
}): string => {
  const isH1 = node.type.name === 'heading' && node.attrs['level'] === 1;

  if (isH1 && node.textContent.trim() === '') {
    return 'Give your document a title...';
  }

  const doc = editor.state.doc;
  let hasBodyText = false;

  doc.descendants((n) => {
    if (n.type.name === 'heading' && n.attrs['level'] === 1) {
      return true;
    }
    if (n.isText && n.text !== undefined && n.text.trim().length > 0) {
      hasBodyText = true;
      return false;
    }
    return true;
  });

  if (hasBodyText) {
    return 'Continue writing...';
  }

  return 'Start writing your document...';
};

export const DocumentStateEditor: React.FC<{
  className?: string;
  editorRef?: React.RefObject<RichTextEditorRef | null>;
  editable?: boolean;
}> = ({ className, editorRef: editorRefProp, editable = true }) => {
  const model = useDocumentModel();
  const saveAction = useAction(SaveDocumentAction);
  const editorState = useWatchableValue(model.editorStateWatchable);
  const {
    options: mentionOptions,
    groups: mentionGroups,
    isLoading: mentionLoading,
    interceptMentionInsert,
    cocoInputMentionDialog,
  } = useCocoMentions({
    excludeDocumentId: model.documentId,
    enableInputs: true,
  });
  const internalEditorRef = useRef<RichTextEditorRef>(null);
  const editorRef = editorRefProp ?? internalEditorRef;
  const openToolModal = useOpenToolModal();
  const openResourceMention = useOpenResourceMention();
  const whoami = useWhoami((x) => x.whoami);
  const commentAuthor = useMemo(
    () => ({ id: whoami.id, name: whoami.name ?? whoami.email }),
    [whoami.id, whoami.name, whoami.email]
  );

  const handleMentionClick = useCallback(
    (data: MentionClickData) => {
      // Artifact-viewable resource mentions open in the side panel — as the page's
      // side panel on the standalone page, or as a sibling artifact tab when this
      // editor is itself embedded as an artifact in a chat (so a nested @document
      // opens beside this one instead of navigating away). Falls back to navigation.
      if (openResourceMention(data)) {
        return;
      }
      if (data.t !== 'tool') {
        return;
      }
      const toolKey = data.toolKey ?? data.name;
      if (toolKey !== undefined && toolKey !== '') {
        openToolModal(toolKey);
      }
    },
    [openResourceMention, openToolModal]
  );

  const { scheduleSave } = useContinualSave({
    save: () =>
      void saveAction.execute({ silent: true, documentId: model.documentId }),
    getEditorStateForSave: () => model.getEditorStateForSave(),
    enabled: editable,
  });

  const hasUserFocusedEditorRef = useRef(false);

  const content =
    editorState !== null ? editorState : { type: 'doc' as const, content: [] };

  const contentKey = editorState === null ? 'empty' : 'loaded';

  const handleUpdate = useCallback(
    (editor: TiptapEditor) => {
      model.editorStateWatchable.set(editor.getJSON());
      if (hasUserFocusedEditorRef.current) {
        model.markUserEdited();
      }
      if (editable) {
        scheduleSave(editor);
      }
    },
    [model, scheduleSave, editable]
  );

  const handleEditorFocus = useCallback(() => {
    hasUserFocusedEditorRef.current = true;
  }, []);

  // Publishes the instance for the comments rail and the header's comment count,
  // which render outside this tree and cannot reach it through `editorRef`.
  const handleEditorReady = useCallback(
    (editor: TiptapEditor | null) => {
      model.editorInstanceWatchable.set(editor);
    },
    [model]
  );

  const editorExtensions = useMemo((): EditorExtensionConfig => {
    return [
      {
        extension: Disclaimer,
        config: {
          menus: ['turn-into', 'drag-context', 'slash'],
        },
      },
      {
        extension: JsonTypeBuilderNode,
        config: {
          menus: ['slash'],
        },
      },
      {
        extension: ExpressionNode.configure({
          parser: undefined,
        }),
        config: {
          menus: ['slash'],
        },
      },
      {
        extension: AttachmentNode.configure({
          items: [],
        }),
        config: {
          menus: ['slash'],
        },
      },
      {
        extension: CoteraMention.configure({
          onClick: handleMentionClick,
        }),
      },
    ];
  }, [handleMentionClick]);

  return (
    <div
      className={cn(
        'transition-all duration-500 ease-in-out w-full relative shrink-0',
        RESOURCE_MEASURE,
        'shadow-none h-full flex flex-col pt-5',
        className
      )}
    >
      <div className="flex-1 min-h-0">
        <RichTextEditor
          key={contentKey}
          ref={editorRef as React.RefObject<RichTextEditorRef>}
          className="h-full"
          editable={editable}
          autoFocus={model.isNew}
          titlePlaceholder="Give your document a title..."
          placeholder={makePlaceholder}
          commentAuthor={commentAuthor}
          onEditorReady={handleEditorReady}
          extensions={editorExtensions}
          content={content}
          onUpdate={handleUpdate}
          onFocus={handleEditorFocus}
        >
          <MentionResourcePicker
            options={mentionOptions}
            groups={mentionGroups}
            isLoading={mentionLoading}
            interceptMentionInsert={interceptMentionInsert}
            anchorToEditorStart={true}
          />
          <EmojiSuggestionMenu />
          {cocoInputMentionDialog}
        </RichTextEditor>
      </div>
    </div>
  );
};
